import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Security() {
  const [settings, setSettings] = useState(null);
  const [newDevice, setNewDevice] = useState("");
  const [newWifi, setNewWifi] = useState("");
  const [newIp, setNewIp] = useState("");
  const [logs, setLogs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    const { data: l } = await api.get("/logs");
    setLogs(l);
  };
  useEffect(() => { load(); }, []);

  const update = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setSettings(data);
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addTo = async (key, value) => {
    if (!value.trim()) return;
    const arr = [...(settings[key] || []), value.trim()];
    await update({ [key]: arr });
  };
  const removeFrom = async (key, value) => {
    const arr = (settings[key] || []).filter((v) => v !== value);
    await update({ [key]: arr });
  };

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="security-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Güvenlik</h1>
        <p className="text-muted-foreground text-sm mt-1">Cihaz, GPS, Wi-Fi ve IP kontrolleri</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Cihaz Yetkilendirme (Device ID)" testid="devices-section">
          <div className="flex gap-2">
            <Input placeholder="HAKIS-QR-01" value={newDevice} onChange={(e) => setNewDevice(e.target.value)} data-testid="new-device" />
            <Button onClick={() => { addTo("device_ids", newDevice); setNewDevice(""); }}>Ekle</Button>
          </div>
          <TagList items={settings.device_ids} onRemove={(v) => removeFrom("device_ids", v)} />
          <p className="text-xs text-muted-foreground">Boş bırakılırsa tüm cihazlardan giriş kabul edilir.</p>
        </Section>

        <Section title="GPS / Konum Kontrolü" testid="gps-section">
          <div className="flex items-center justify-between">
            <Label>Konum kontrolünü etkinleştir</Label>
            <Switch checked={!!settings.gps_enabled} onCheckedChange={(v) => update({ gps_enabled: v })} data-testid="gps-switch" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Latitude</Label>
              <Input type="number" step="0.0001" value={settings.store_lat}
                onChange={(e) => setSettings({ ...settings, store_lat: parseFloat(e.target.value) })}
                onBlur={() => update({ store_lat: settings.store_lat })} />
            </div>
            <div className="space-y-1"><Label className="text-xs">Longitude</Label>
              <Input type="number" step="0.0001" value={settings.store_lng}
                onChange={(e) => setSettings({ ...settings, store_lng: parseFloat(e.target.value) })}
                onBlur={() => update({ store_lng: settings.store_lng })} />
            </div>
            <div className="space-y-1"><Label className="text-xs">Yarıçap (m)</Label>
              <Input type="number" value={settings.store_radius}
                onChange={(e) => setSettings({ ...settings, store_radius: parseInt(e.target.value) || 0 })}
                onBlur={() => update({ store_radius: settings.store_radius })} />
            </div>
          </div>
        </Section>

        <Section title="Wi-Fi SSID Kısıtlaması" testid="wifi-section">
          <div className="flex gap-2">
            <Input placeholder="HAKIS-OYUNCAK" value={newWifi} onChange={(e) => setNewWifi(e.target.value)} data-testid="new-wifi" />
            <Button onClick={() => { addTo("wifi_ssids", newWifi); setNewWifi(""); }}>Ekle</Button>
          </div>
          <TagList items={settings.wifi_ssids} onRemove={(v) => removeFrom("wifi_ssids", v)} />
        </Section>

        <Section title="İzinli IP Adresleri" testid="ip-section">
          <div className="flex gap-2">
            <Input placeholder="10.0.0.1" value={newIp} onChange={(e) => setNewIp(e.target.value)} data-testid="new-ip" />
            <Button onClick={() => { addTo("allowed_ips", newIp); setNewIp(""); }}>Ekle</Button>
          </div>
          <TagList items={settings.allowed_ips} onRemove={(v) => removeFrom("allowed_ips", v)} />
        </Section>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border/60 font-display font-semibold tracking-tight">Güvenlik Log Kayıtları</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Zaman</TableHead>
              <TableHead>Aksiyon</TableHead>
              <TableHead>Sonuç</TableHead>
              <TableHead>Cihaz</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>GPS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.slice(0, 60).map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs">{new Date(l.timestamp).toLocaleString("tr-TR")}</TableCell>
                <TableCell>{l.action}</TableCell>
                <TableCell><span className={l.result === "success" ? "text-emerald-500" : "text-red-500"}>{l.result}</span></TableCell>
                <TableCell className="font-mono text-xs">{l.device_id || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{l.ip || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{l.gps || "—"}</TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Log kaydı yok.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="glass-card rounded-xl p-5 space-y-3" data-testid={testid}>
      <div className="font-display font-semibold tracking-tight">{title}</div>
      {children}
    </div>
  );
}
function TagList({ items = [], onRemove }) {
  if (items.length === 0) return <div className="text-xs text-muted-foreground">Kayıt yok.</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((v) => (
        <span key={v} className="px-3 py-1 rounded-full bg-secondary text-sm flex items-center gap-2">
          {v}
          <button onClick={() => onRemove(v)} className="text-muted-foreground hover:text-red-500">×</button>
        </span>
      ))}
    </div>
  );
}
